<template>
  <div class="training-zone-wrapper">
    <customer-appointment-home />

    <v-card v-if="showAlibeyHint" class="alibey-hint-card mb-3" flat>
      <div class="alibey-hint-icon-wrapper">
        <v-icon color="white" size="22">mdi-clock-outline</v-icon>
      </div>
      <div class="alibey-hint-content">
        <div class="alibey-hint-title">Kursplan in Vorbereitung</div>
        <div class="alibey-hint-subtitle">Hier siehst du eine Vorschau ab {{ $moment(alibeyStartDate).format("DD.MM.") }}</div>
      </div>
    </v-card>
    <div class="transparent-bg" style="padding-bottom: 100px; overflow: visible">
      <v-skeleton-loader
        v-if="loadingCourses"
        dark
        height="150"
        type="list-item-avatar-two-line"
        class="rounded-xl custom-skeleton mb-2"></v-skeleton-loader>

      <div v-else>
        <div v-if="visibleCourseList.length > 0 && !showBergCourseNotice">
          <div>
            <div v-for="course in visibleCourseList" :key="course._id" class="mb-3">
              <v-card
                class="rounded-xl gf-list-card d-flex align-center pr-4"
                :class="{ 'signed-up-border': course.signedUp }"
                @click="pushToCourse(course)"
                v-ripple
                style="position: relative; overflow: visible">
                <v-chip
                  v-if="course.signedUp && !course.onQueue"
                  small
                  color="primary"
                  style="position: absolute; height: 20px; top: -10px; left: 50%; transform: translateX(-50%); z-index: 5"
                  class="elevation-5">
                  <span style="text-transform: uppercase; font-weight: 900; font-style: italic; font-size: 0.7rem; letter-spacing: 1px"
                    >Angemeldet</span
                  >
                </v-chip>

                <v-chip
                  v-if="course.onQueue"
                  small
                  color="orange"
                  style="position: absolute; height: 20px; top: -10px; left: 50%; transform: translateX(-50%); z-index: 5"
                  class="elevation-5">
                  <span
                    style="color: white; text-transform: uppercase; font-weight: 900; font-style: italic; font-size: 0.7rem; letter-spacing: 1px"
                    >Warteliste</span
                  >
                </v-chip>

                <v-img
                  :src="course.course.bild ? '/img/kurse/' + course.course.bild : '/img/kurse/bodypump.jpg'"
                  height="85"
                  width="85"
                  class="rounded-l-xl mr-4"
                  style="max-width: 85px"
                  cover>
                  <div class="fill-height d-flex align-end justify-start pa-0">
                    <div class="time-badge">{{ course.duration }} Min</div>
                  </div>
                </v-img>

                <div class="flex-grow-1 py-2 overflow-hidden">
                  <div class="d-flex align-center mb-1">
                    <v-chip x-small label color="rgba(82, 174, 50, 0.2)" class="primary--text font-weight-bold px-2 mr-2">
                      {{ courseStartDate(course) }} Uhr
                    </v-chip>
                  </div>

                  <h4 class="white--text font-weight-bold text-uppercase text-body-1 leading-tight text-truncate mb-1">
                    {{ course.course.name }}
                  </h4>

                  <div class="d-flex align-center caption grey--text text--lighten-1">
                    <v-icon x-small color="grey" class="mr-1">mdi-account-outline</v-icon>
                    <span class="text-truncate">
                      {{ getInstructorName(course) }}
                    </span>
                  </div>
                </div>

                <div class="d-flex flex-column align-center ml-2">
                  <div class="capacity-ring">
                    <v-progress-circular
                      :value="calcPercentage(course)"
                      :color="getIndicatorColor(calcPercentage(course))"
                      size="44"
                      width="3"
                      rotate="-90">
                      <span class="capacity-text" style="font-size: 0.75rem"> {{ course.signupAmount || 0 }}/{{ course.amount || 0 }} </span>
                    </v-progress-circular>
                  </div>
                </div>
              </v-card>
            </div>
          </div>

          <div class="text-center mt-4">
            <v-btn text small color="grey lighten-1" @click="$router.push('/kurse')">
              Zum kompletten Plan <v-icon right x-small>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>
        <div v-else-if="showBergCourseNotice" class="text-center pa-8 glass-card rounded-xl preparation-state">
          <div class="icon-pulse-wrapper mb-4">
            <v-icon size="50" color="primary">mdi-hammer-wrench</v-icon>
          </div>
          <h3 class="white--text font-weight-black text-h6 mb-2">Kursplan in Vorbereitung</h3>
          <p class="berg-course-notice-text grey--text text--lighten-1 mb-0">
            Für unser bald eröffnendes Studio in Berg wird gerade ein neuer Group Fitness Kursplan vorbereitet. Sobald er bereit ist, findest du ihn hier.
          </p>

          <div class="glow-bg"></div>
        </div>
        <div v-else class="text-center pa-10 glass-card rounded-xl">
          <v-icon size="60" color="grey darken-2" class="mb-4">mdi-calendar-sleep</v-icon>
          <h4 class="white--text font-weight-bold">Feierabend für heute!</h4>
          <p class="caption grey--text mb-6">Für heute sind keine Kurse mehr geplant.</p>
          <v-btn rounded outlined color="primary" @click="$router.push('/kurse')">Morgen checken</v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import { compactCourses, readOverview } from "@/store/homeOverviewStore"
import { mapState } from "vuex"
import userService from "@/services/userService"
import CustomerAppointmentHome from "./CustomerAppointmentHome.vue"
export default {
  name: "TrainingZone",
  components: {
    CustomerAppointmentHome,
  },
  data() {
    return {
      userService,
      loadingCourses: true,
      visibleCourseList: [],
      abortController: null,
      courseRequestVersion: 0,
    }
  },
  computed: {
    ...mapState("userStore", ["user"]),
    ...mapState("studioStore", ["selectedStudio"]),
    alibeyStudio() {
      const code = this.selectedStudio?.code?.toLowerCase()
      return code === "alibey" || code === "ali"
    },
    studioCode() {
      const code = this.selectedStudio?.code?.toLowerCase()
      return code
    },
    showBergCourseNotice() {
      // Temporäre Sonderansicht, bis der Group Fitness Kursplan für Berg bereit ist.
      return this.studioCode === "berg" && !this.userService.hasRight("stat_gf_stud")
    },

    alibeyStartDate() {
      return "2026-05-30"
    },

    alibeyEndDate() {
      return "2026-06-05"
    },
    showAlibeyHint() {
      return false
      if (!this.alibeyStudio) return false
      if (this.userService.hasRight("gf_f_courses")) return false
      // Nur vor Start des Zeitraums anzeigen
      return this.$moment().isBefore(this.$moment(this.alibeyStartDate, "YYYY-MM-DD"))
    },
    sirnachStudio() {
      return this.user && this.user.studio_id === "sirnach"
    },
  },
  watch: {
    "selectedStudio._id": "fetchCourses",
    "$store.state.userStore.user._id": "fetchCourses",
  },
  created() {
    this._coursesAlive = true
    this.fetchCourses()
  },
  beforeDestroy() {
    this._coursesAlive = false
    this.courseRequestVersion++
    if (this.abortController) this.abortController.abort()
  },
  methods: {
    courseTargetTs() {
      const today = this.$moment().startOf("day")
      if (!this.alibeyStudio) return today.unix()
      const start = this.$moment(this.alibeyStartDate, "YYYY-MM-DD").startOf("day")
      const end = this.$moment(this.alibeyEndDate, "YYYY-MM-DD").startOf("day")
      return (today.isBefore(start) ? start : today.isAfter(end) ? end : today).unix()
    },
    courseScope() {
      const owner = userService.getUserId()
      const studio = this.selectedStudio?._id
      return owner && studio ? JSON.stringify([owner, studio, this.$moment().format("YYYY-MM-DD"), this.courseTargetTs(), this.showBergCourseNotice]) : null
    },
    filterCourses(courses) {
      const now = this.$moment()
      return courses.filter((course) => {
        const end = this.$moment().startOf("day").set({ hours: course.hours, minutes: course.minutes }).add(course.duration, "minutes")
        return !course.cancelled && (this.alibeyStudio || now.isBefore(end))
      })
    },
    async fetchCourses() {
      if (!this._coursesAlive) return
      const version = ++this.courseRequestVersion
      if (this.abortController) this.abortController.abort()
      this.abortController = new AbortController()
      const scope = this.courseScope()
      const token = localStorage.getItem("token")
      const current = () => this._coursesAlive && version === this.courseRequestVersion && scope === this.courseScope() && token === localStorage.getItem("token")
      const cached = readOverview(this.$store.state.homeOverviewStore, "courses", scope)
      this.visibleCourseList = this.filterCourses(cached || [])
      this.loadingCourses = cached === null
      try {
        const data = await api.fetchCourseList(this.abortController.signal, this.courseTargetTs())
        if (!current()) return
        const courses = compactCourses(data)
        this.visibleCourseList = this.filterCourses(courses)
        this.$store.commit("homeOverviewStore/save", { key: "courses", scope, data: courses })
      } catch (e) {
        if (!current()) return
        if ([401, 403].includes(e.response?.status)) {
          this.visibleCourseList = []
          this.$store.commit("homeOverviewStore/clear", { key: "courses", scope })
        }
        if (e.name !== "AbortError") console.error(e)
      } finally {
        if (current()) this.loadingCourses = false
      }
    },
    courseStartDate(course) {
      return this.$moment().set({ hours: course.hours, minutes: course.minutes }).format("HH:mm")
    },
    getInstructorName(course) {
      if (course.instructors && course.instructors.length > 0) {
        return course.instructors[0].vorname
      }
      return "Team"
    },
    pushToCourse(course) {
      if (this.showAlibeyHint) return
      if (this.sirnachStudio) return this.$router.push("/kurse")

      // Bei Alibey kein "Past"-Check (Kurse sind ja in der Zukunft)
      if (!this.alibeyStudio) {
        const courseEndTime = this.$moment().set({ hours: course.hours, minutes: course.minutes }).add(course.duration, "minutes")
        if (this.$moment().isAfter(courseEndTime)) return
      }

      const targetTs = this.courseTargetTs()

      let ts = course.courseTimelineId ? 0 : targetTs

      this.$router.push({
        name: "courseListView",
        params: { id: course._id, ts },
        query: { home: true },
      })
    },
    calcPercentage(course) {
      if (course.amount > 0) {
        // Alte Logik: Freie Plätze berechnen
        const free = course.amount - (course.signupAmount || 0)
        // Prozent der FREIEN Plätze (für die Farbe/Kreis)
        return Math.round((free / course.amount) * 100)
      }
      return 100
    },
    // Farbe passend dazu (wenig Platz = Rot)
    getIndicatorColor(percentage) {
      if (percentage <= 10) return "red"
      if (percentage <= 50) return "orange"
      return "primary"
    },
  },
}
</script>

<style lang="scss" scoped>
.alibey-hint-card {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(82, 174, 50, 0.18) 0%, rgba(82, 174, 50, 0.08) 100%) !important;
  border: 1px solid rgba(82, 174, 50, 0.3) !important;
  border-radius: 14px !important;
  padding: 12px 14px;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -30px;
    right: -30px;
    width: 100px;
    height: 100px;
    background: rgba(82, 174, 50, 0.1);
    border-radius: 50%;
  }
}

.alibey-hint-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--v-primary) 0%, #00b84c 100%);
  margin-right: 12px;
  box-shadow: 0 4px 12px rgba(82, 174, 50, 0.3);
  position: relative;
  z-index: 1;

  .v-icon {
    animation: pulse-icon 2s ease-in-out infinite;
  }
}

.alibey-hint-content {
  flex: 1;
  position: relative;
  z-index: 1;
}

.alibey-hint-title {
  font-size: 0.9rem;
  font-weight: 800;
  color: white;
  letter-spacing: 0.3px;
  line-height: 1.2;
}

.alibey-hint-subtitle {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 2px;
}

@keyframes pulse-icon {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}
/* VARIABLES */
$primary-color: var(--v-primary, #52ae32);
$glass-bg: rgba(255, 255, 255, 0.05);
$glass-border: rgba(255, 255, 255, 0.08);

.signed-up-border {
  border-left: 4px solid $primary-color !important;
  /* Optional: Leichter Background-Glow für angemeldete Kurse */
  background: linear-gradient(90deg, rgba(82, 174, 50, 0.15) 0%, rgba(255, 255, 255, 0.04) 30%) !important;
}

.italic-header {
  font-style: italic;
  letter-spacing: -0.5px;
  text-shadow: 0 4px 15px rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
}
.transparent-bg {
  background: transparent !important;
}
.relative {
  position: relative;
}
.z-index-2 {
  z-index: 2;
}

/* COURSE CARD */
.gf-list-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.2s;
  &:active {
    background: rgba(255, 255, 255, 0.08);
    transform: scale(0.98);
  }
}
.time-badge {
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  color: white;
  font-size: 0.65rem;
  font-weight: bold;
  padding: 2px 8px;
  border-top-right-radius: 10px;
}
.capacity-text {
  font-size: 0.9rem;
  font-weight: 900;
  color: white;
}

/* SKELETON FIX */
.custom-skeleton {
  background: transparent !important;
}
/* Aggressives Überschreiben für Vuetify Skeletons */
::v-deep .v-skeleton-loader__bone {
  background: rgba(255, 255, 255, 0.08) !important;
}
::v-deep .v-skeleton-loader__image,
::v-deep .v-skeleton-loader__avatar,
::v-deep .v-skeleton-loader__text {
  background: rgba(255, 255, 255, 0.08) !important;
}
/* Falls Vuetify Theme Light ist, erzwingen wir hier dunkle Farben */
::v-deep .theme--light.v-skeleton-loader .v-skeleton-loader__bone:after {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.05), transparent) !important;
}

/* Utils */
.glass-card {
  background: $glass-bg !important;
  backdrop-filter: blur(25px);
  border: 1px solid $glass-border;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.5);
}
.preparation-state {
  position: relative;
  overflow: hidden;
}
.preparation-state > *:not(.glow-bg) {
  position: relative;
  z-index: 1;
}
.berg-course-notice-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
  line-height: 1.55;
}
.leading-tight {
  line-height: 1.1;
}
</style>
